import type { ContentfulClientApi, Entry, EntryCollection } from 'contentful';
import type { Limit } from 'p-limit';

export type RecurseParentsContext<T> = {
  originalId: string;
  limit: Limit;
  crawledIds: Map<string, EntryCollection<T> | null>;
  contentType: string;
  timeout: number;
  deadline: bigint;
};

export type RecurseParentsResult<T> = {
  /**
   * All entries that were found while crawling parent entries which matched the given contentType.
   */
  found: Entry<T>[];
  /**
   * The total number of queries that were made to Contentful.
   */
  totalQueries: number;
  /**
   * The number of queries that were skipped because the entry had already been crawled.
   */
  skippedDuplicateQueries: number;
};

export async function recurseParents<T>(
  id: string,
  client: ContentfulClientApi,
  context: RecurseParentsContext<T>,
  result: RecurseParentsResult<T> = {
    found: [],
    totalQueries: 0,
    skippedDuplicateQueries: 0,
  },
) {
  if (process.hrtime.bigint() > context.deadline) {
    throw new Error(
      `recurseParents called with ${context.originalId} still recursing parents after ${context.timeout}s. Aborting.`,
    );
  }

  // This prevents both infinite loops and duplicate results
  if (context.crawledIds.has(id)) {
    result.skippedDuplicateQueries++;
    return result;
  }

  context.crawledIds.set(id, null);
  result.totalQueries++;
  const res = await context.limit(() =>
    client.getEntries<T>({
      links_to_entry: id,
    }),
  );
  context.crawledIds.set(id, res);

  if (res?.items?.length > 0) {
    await Promise.all(
      res.items.map((item) => {
        if (item.sys.contentType.sys.id === context.contentType) {
          result.found.push(item);
        } else {
          return recurseParents(item.sys.id, client, context, result);
        }
      }),
    );
  }

  return result;
}
